import { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faMagnifyingGlass,
    faTimes,
    faClock,
    faArrowRight,
    faFileAlt,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons'
import type { KbSearchResult } from '../types'

interface SearchOverlayProps {
    isOpen: boolean
    onClose: () => void
    onSelectArticle: (slug: string) => void
    onSearch: (query: string) => Promise<KbSearchResult[]>
}

const SearchOverlay = ({
    isOpen,
    onClose,
    onSelectArticle,
    onSearch,
}: SearchOverlayProps) => {
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<KbSearchResult[]>([])
    const [searching, setSearching] = useState(false)
    const [searched, setSearched] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        if (isOpen) {
            setQuery('')
            setResults([])
            setSearched(false)
            setTimeout(() => inputRef.current?.focus(), 100)
        }
    }, [isOpen])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isOpen, onClose])

    const handleQueryChange = (val: string) => {
        setQuery(val)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        if (!val.trim()) {
            setResults([])
            setSearched(false)
            return
        }
        setSearching(true)
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await onSearch(val)
                setResults(res)
                setSearched(true)
            } catch {
                setResults([])
                setSearched(true)
            } finally {
                setSearching(false)
            }
        }, 300)
    }

    if (!isOpen) return null

    const handleSelect = (slug: string) => {
        onSelectArticle(slug)
        onClose()
    }

    return (
        <div className="kb-search-overlay" onClick={onClose}>
            <div
                className="kb-search-overlay__dialog"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="kb-search-overlay__input-wrapper">
                    <FontAwesomeIcon
                        icon={searching ? faSpinner : faMagnifyingGlass}
                        spin={searching}
                        className="kb-search-overlay__input-icon"
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        className="kb-search-overlay__input"
                        placeholder="Search the knowledge base..."
                        value={query}
                        onChange={(e) => handleQueryChange(e.target.value)}
                    />
                    <button
                        className="kb-search-overlay__close"
                        onClick={onClose}
                    >
                        <kbd>ESC</kbd>
                    </button>
                </div>

                {searched && query.length > 0 && (
                    <div className="kb-search-overlay__results">
                        {results.length === 0 ? (
                            <div className="kb-search-overlay__empty">
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlass}
                                    className="kb-search-overlay__empty-icon"
                                />
                                <p>
                                    No results found for "
                                    <strong>{query}</strong>"
                                </p>
                                <span>
                                    Try different keywords or browse categories
                                </span>
                            </div>
                        ) : (
                            <>
                                <div className="kb-search-overlay__count">
                                    {results.length}{' '}
                                    {results.length === 1
                                        ? 'result'
                                        : 'results'}{' '}
                                    found
                                </div>
                                {results.map((result) => (
                                    <button
                                        key={result.id}
                                        className="kb-search-overlay__result"
                                        onClick={() =>
                                            handleSelect(result.slug)
                                        }
                                    >
                                        <div className="kb-search-overlay__result-icon">
                                            <FontAwesomeIcon
                                                icon={faFileAlt}
                                            />
                                        </div>
                                        <div className="kb-search-overlay__result-content">
                                            <h4>{result.title}</h4>
                                            <p>{result.excerpt}</p>
                                            <span className="kb-search-overlay__result-meta">
                                                <span className="kb-search-overlay__result-cat">
                                                    {result.category_title}
                                                </span>
                                                <span>
                                                    <FontAwesomeIcon
                                                        icon={faClock}
                                                        className="me-1"
                                                    />
                                                    {result.reading_time} min
                                                </span>
                                            </span>
                                        </div>
                                        <FontAwesomeIcon
                                            icon={faArrowRight}
                                            className="kb-search-overlay__result-arrow"
                                        />
                                    </button>
                                ))}
                            </>
                        )}
                    </div>
                )}

                {!searched && query.length === 0 && (
                    <div className="kb-search-overlay__hint">
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="me-2"
                            style={{ opacity: 0 }}
                        />
                        <span>
                            Start typing to search across all articles,
                            guides, and FAQs
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SearchOverlay
